import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { AutoSelectionForm } from '@/components/autoforms/AutoSelectionForm';
import { VoiceInputButton } from '@/components/forms/FormFieldRenderer';
import { getSDK } from '@/store/sdkStore';
import { friendlyError } from '@/lib/errors';
import { eventLog } from '@/lib/eventLog';

// ============================================================================
// AddressField — «Дополнение адреса» (вид поля; builtin:address, реестр
// дополнений платформы). Работает с РЕКВИЗИТОМ-ССЫЛКОЙ (mode='ref', значение
// id адреса) И с ЛЮБЫМ ТЕКСТОВЫМ ПОЛЕМ (mode='text', значение — строка) —
// встраивается в любую форму (default_for: reference/addresses + text).
// Ввод с автодополнением (ExternalAPI suggest-address), «...» — выбор из
// справочника «Адреса» (вкл/выкл — настройка обложки picker).
// Детерминизм: подсказки недоступны/провайдер не настроен — ОШИБКА В ЖУРНАЛ
// регистрации (eventLog), ручной ввод продолжается (создание записи НЕ
// блокируется); в mode='text' при выборе подсказки — фоновый свод в «Адреса»
// (best-effort; ошибка — только журнал).
// ============================================================================

interface AddressFieldProps {
  value: unknown;
  displayValue?: string;
  onChange: (v: unknown) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Кнопка выбора «...» (настройка обложки; по умолчанию вкл) */
  picker?: boolean;
  /** 'ref' — реквизит-ссылка (id адреса); 'text' — текстовое поле (строка) */
  mode?: 'ref' | 'text';
  /** Голосовой ввод (микрофон; у заявок — все текстовые поля): распознанный
   *  текст дописывается в поле, подсказки «Адресов» подхватывают его сами. */
  voiceInput?: boolean;
}

interface SuggestItem {
  value: string;
  unrestricted_value: string;
  /** Локальный вариант (из справочника «Адреса»): id уже существует — свод НЕ нужен */
  id?: string;
  [k: string]: unknown;
}

export function AddressField({ value, displayValue, onChange, disabled, placeholder, picker = true, mode = 'ref', voiceInput = false }: AddressFieldProps) {
  const isText = mode === 'text';
  // Текст поля: для ref — представление выбранного адреса (локальный state,
  // т.к. источник значения — id, а текст рисуем из displayValue);
  // для text — НЕ храним отдельно: источник — value родителя (customValues),
  // каждый ввод сразу уходит наверх через onChange (как обычное текстовое
  // поле). Никаких таймеров/гонок blur↔«Сохранить».
  const [text, setText] = useState('');
  const [suggestions, setSuggestions] = useState<SuggestItem[]>([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  // Какая подсказка активна (клавиатура ↑/↓): -1 = нет; 0..N-1 — строка списка
  const [activeIdx, setActiveIdx] = useState(-1);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const activeItemRef = useRef<HTMLButtonElement | null>(null);

  // Текущий текст в поле. Для text-режима источник — value родителя
  // (customValues): приходит от onChange на каждый символ. Для ref — локальное
  // представление выбранного адреса (displayValue), пока пользователь не начал
  // вводить новый текст (ищем/сводим на blur).
  const textValue = isText ? String(value ?? '') : text;

  // Сброс локального текста (ref-режим) при смене выбранного значения/представления
  useEffect(() => {
    if (!isText) setText(displayValue || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayValue, isText]);

  // Сброс активной подсказки при новом списке
  useEffect(() => { setActiveIdx(-1); }, [suggestions]);

  // Активная строка подсказки всегда в видимой области (клавиатура ↑/↓,
  // длинный список скроллится)
  useEffect(() => {
    if (activeIdx >= 0 && activeItemRef.current) {
      activeItemRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIdx]);

  // Автодополнение подсказок: СПРАВОЧНИК «Адреса» (SSoT-сервер,
  // SuggestReference — работает всегда) + ВНЕШНИЕ API (suggestAddress, если
  // настроены). Ошибка внешнего API — журнал, подсказки из справочника
  // продолжают работать; ручной ввод не блокируется.
  // Событийная модель БЕЗ таймеров: каждое изменение текста сразу запускает
  // запрос; устаревший ответ отбрасывается ПОКОЛЕНИЕМ через ref (а НЕ state —
  // замыкание функции видит актуальное значение; useState давал gen со старого
  // рендера → проверка myGen !== gen ВСЕГДА true → подсказки не появлялись).
  const genRef = useRef(0);
  const loadSuggestions = async (q: string) => {
    const myGen = ++genRef.current;
    if (!q.trim()) { setSuggestions([]); setSuggestOpen(false); return; }
    // Лок-первый (экономия внешних вызовов): справочник «Адреса» достаточен —
    // внешний API не дёргаем; иначе добираем из ExternalAPI и мержим
    const localP = getSDK().metadata.suggestReference({ refTable: 'addresses', query: q, limit: 8 } as any)
      .then((r: any) => ((r as any)?.items || []).map((it: any) => ({ value: String(it.name || ''), unrestricted_value: String(it.name || ''), id: String(it.id || '') })))
      .catch((e: any) => { void eventLog.error('ui', 'Дополнение адреса: подбор из справочника недоступен', { details: friendlyError(e) }); return []; });
    const local = await localP;
    if (myGen !== genRef.current) return; // устаревший ответ (текст уже изменился)
    let ext: SuggestItem[] = [];
    if (local.length < 8) {
      ext = await getSDK().geo.suggestAddress({ query: q, count: 8 } as any)
        .then((r: any) => ((r as any)?.items || []) as SuggestItem[])
        .catch((e: any) => { void eventLog.error('ui', 'Дополнение адреса: внешнее автодополнение недоступно', { details: friendlyError(e) }); return []; });
      if (myGen !== genRef.current) return;
    }
    // Объединение с дедупом по тексту: сначала справочник (уже в базе), потом внешние
    const seen = new Set<string>();
    const merged: SuggestItem[] = [];
    for (const it of [...local, ...ext]) {
      const key = String(it.value || it.unrestricted_value || '').trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      merged.push(it);
    }
    if (myGen !== genRef.current) return;
    setSuggestions(merged.slice(0, 8));
    setSuggestOpen(merged.length > 0);
  };

  // Ввод текста: text-режим — сразу в значение формы (onChange), ref — локально.
  const onTextChange = (v: string) => {
    if (isText) {
      onChange(v);
    } else {
      setText(v);
    }
    void loadSuggestions(v);
  };

  // Применить адрес: текстовое поле — всегда строка-адрес (модуль объекта при
  // сохранении сводит текст в «Адреса»); ref-поле — id записи «Адреса».
  const applyAddress = (addressId: string, name: string) => {
    if (isText) {
      onChange(name);
    } else {
      onChange(addressId);
      setText(name);
    }
    setSuggestions([]);
    setSuggestOpen(false);
    setActiveIdx(-1);
  };

  // Клавиатура в поле ввода (как в 1С/стандартных автодополнениях):
  // ↑/↓ — перемещение по подсказкам, Enter — выбор активной, Esc — закрыть список.
  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!suggestOpen || suggestions.length === 0) {
      // Список закрыт — Enter/Esc не перехватываем
      if (e.key === 'Escape') setSuggestOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const idx = activeIdx >= 0 ? activeIdx : 0;
      const s = suggestions[idx];
      if (s) { void resolveAndSet(s); }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setSuggestOpen(false);
      setActiveIdx(-1);
    }
  };

  const resolveAndSet = async (suggestion: SuggestItem) => {
    setBusy(true);
    try {
      if (suggestion.id) {
        // Уже в справочнике «Адреса» — свод не нужен (детерминированно)
        const name = suggestion.value || '';
        applyAddress(suggestion.id, name);
        return;
      }
      const r: any = await getSDK().geo.resolve({ suggestion } as any);
      const name = r?.addressName || suggestion.value || '';
      const addressId = r?.addressId || '';
      if (isText) {
        // Текстовое поле: подставляем адрес одной строкой; свод прошёл —
        // адрес уже в справочнике (модуль объекта при сохранении найдёт его
        // по тексту и поставит address_id). Если свод недоступен — текст
        // сохраняется как есть.
        onChange(name);
      } else if (addressId) {
        onChange(addressId);
        setText(name);
      }
    } catch (e: any) {
      // Свод недоступен — вручную оставляем текст (модуль объекта при
      // сохранении найдёт/создаст адрес сам); создание записи не блокируем
      const name = suggestion.value || suggestion.unrestricted_value || '';
      if (isText) {
        onChange(name);
      }
      void eventLog.error('ui', 'Дополнение адреса: не удалось свести адрес в справочник', { details: friendlyError(e) });
    } finally {
      setBusy(false);
    }
  };

  // Потеря фокуса. Текст (text-режим) УЖЕ в значении формы (onChange на каждый
  // символ) — здесь только закрываем подсказки, БЕЗ таймеров/гонок с «Сохранить».
  // ref-поле: незаполненный текст → первая подсказка → id. Сначала ЛОКАЛЬНЫЙ
  // поиск по справочнику (уже в базе — свод не нужен), затем внешний (свод).
  // Ошибка — журнал, не блокирует.
  const onBlur = async () => {
    const q = textValue.trim();
    if (isText) {
      setSuggestions([]);
      setSuggestOpen(false);
      return;
    }
    if (busy || !q) return;
    try {
      const local: any = await getSDK().metadata.suggestReference({ refTable: 'addresses', query: q, limit: 1 } as any);
      const localItems = ((local as any)?.items || []) as SuggestItem[];
      if (localItems.length > 0) {
        await resolveAndSet({ value: String(localItems[0].name || ''), unrestricted_value: String(localItems[0].name || ''), id: String(localItems[0].id || '') });
        return;
      }
      const r: any = await getSDK().geo.suggestAddress({ query: q, count: 1 } as any);
      const items = ((r as any)?.items || []) as SuggestItem[];
      if (items.length > 0) {
        await resolveAndSet(items[0]);
      } else {
        void eventLog.warning('ui', 'Дополнение адреса: адрес не найден — выберите из подсказок');
      }
    } catch (e: any) {
      void eventLog.error('ui', 'Дополнение адреса: проверка адреса недоступна', { details: friendlyError(e) });
    }
  };

  const clear = () => {
    if (isText) {
      onChange('');
    } else {
      onChange('');
      setText('');
    }
    setSuggestions([]);
    setSuggestOpen(false);
    setActiveIdx(-1);
  };

  return (
    <>
      <div className="flex gap-1 w-full">
        <div className="relative flex-1 min-w-0">
          <Input
            ref={inputRef}
            value={textValue}
            onChange={(e) => onTextChange(e.target.value)}
            onKeyDown={onInputKeyDown}
            onBlur={() => { void onBlur(); }}
            placeholder={placeholder || 'Введите адрес…'}
            disabled={disabled}
            className="h-8 w-full text-[16px] sm:text-sm"
            autoComplete="off"
            role="combobox"
            aria-expanded={suggestOpen}
            aria-autocomplete="list"
          />
          {suggestOpen && (
            <div ref={listRef} className="absolute z-30 mt-1 w-full rounded-lg border bg-popover shadow-lg max-h-64 overflow-y-auto py-1">
              {suggestions.map((s, i) => (
                <button
                  key={`${s.id || ''}-${i}`}
                  ref={i === activeIdx ? activeItemRef : undefined}
                  type="button"
                  className={`w-full text-left px-3 py-3 text-[15px] leading-snug border-b last:border-b-0 transition-colors min-h-[44px] flex flex-col justify-center ${i === activeIdx ? 'bg-primary/10 text-foreground' : 'active:bg-accent hover:bg-accent'}`}
                  onMouseEnter={() => setActiveIdx(i)}
                  onMouseDown={(e) => { e.preventDefault(); void resolveAndSet(s); setSuggestOpen(false); }}
                  onTouchEnd={(e) => { e.preventDefault(); void resolveAndSet(s); }}
                >
                  <span className="block">{s.value || s.unrestricted_value}</span>
                  {s.id && <span className="block text-[10px] text-muted-foreground mt-0.5">Уже в справочнике</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        {!disabled && picker && (
          <Button size="sm" variant="outline" className="h-8 px-2 shrink-0" onClick={() => setPickerOpen(true)} title="Выбрать адрес из справочника">
            ...
          </Button>
        )}
        {!disabled && voiceInput && (
          <VoiceInputButton value={textValue || ''} onValue={(full) => onTextChange(full)} />
        )}
        {!disabled && ((isText && textValue) || (!isText && value !== undefined && value !== null && value !== '')) && (
          <Button size="sm" variant="ghost" className="h-8 px-1.5 shrink-0 text-muted-foreground hover:text-destructive" onClick={clear} title="Очистить">
            ✕
          </Button>
        )}
      </div>

      {pickerOpen && (
        <UniversalWindow open onClose={() => setPickerOpen(false)} title="Выбор адреса" blocking width="max-w-2xl">
          <AutoSelectionForm
            objectType="catalog"
            objectName="addresses"
            onSelect={(id, name) => {
              // ДИАГНОСТИКА (временная): доходит ли выбор пикера
              void import('@/lib/eventLog').then((m) => m.eventLog.warning('ui', `[pick2] id=${String(id).slice(0,8)} name=${String(name).slice(0,40)} isText=${isText} v=${String(value ?? '').slice(0,30)}`));
              if (isText) {
                onChange(name || id);
              } else {
                onChange(id);
                setText(name || '');
              }
              setSuggestions([]);
              setSuggestOpen(false);
              setPickerOpen(false);
            }}
            filters={[{ field: 'is_marked', condition: '=', value: 'false' }]}
          />
        </UniversalWindow>
      )}
    </>
  );
}
